import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Layers, Edit, Search, GripVertical, X, Plus, ArrowUp, ArrowDown, Upload, Image } from 'lucide-react';
import { getContentLibraries, getVideoSeriesCategoriesForLibraries, ContentLibraryData, addVideoSeries, updateVideoSeries, uploadSeriesImage, getAllContent, getVimeoContent, getPrograms, Timestamp } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { captureHandledException } from '@/lib/sentry';

interface EpisodeData {
  contentId: string;
  episode: number;
  episodeAddedDate?: any;
}

interface EpisodesMap {
  [contentId: string]: EpisodeData;
}

interface SummaryNextStep {
  contentId: string;
  durationMinutes?: number;
  externalId?: string;
  mediaType: string;
  thumbnailUrl?: string;
  title: string;
}

interface SummaryNextStepsMap {
  [contentId: string]: SummaryNextStep;
}

interface SummaryProgram {
  programId: string;
  name: string;
  thumbnailUrl?: string;
}

interface SummaryProgramsMap {
  [programId: string]: SummaryProgram;
}

interface ProgramItem {
  id: string;
  name: string;
  thumbnailUrl?: string;
}

interface SummaryAction {
  title: string;
  description: string;
}

interface VideoSeriesData {
  id: string;
  title: string;
  description?: string;
  contentLibraries?: string[];
  modules?: string[];
  episodes?: EpisodesMap;
  thumbnailUrl?: string;
  mediaType: string;
  publishDate?: any;
  status?: 'Draft' | 'Published';
  summaryText?: string;
  summaryActions?: SummaryAction[];
  summaryNextSteps?: SummaryNextStepsMap;
  summaryPrograms?: SummaryProgramsMap;
  contributorIds?: string[];
  contributors?: ContributorItem[];
  contentCategories?: string[];
}

interface ContributorItem {
  id: string;
  name: string;
  profilePicUrl?: string;
  qualifications?: string;
  story?: string;
}

interface ContentItem {
  id: string;
  title: string;
  author?: string;
  duration?: string;
  durationMinutes?: number;
  externalId?: string;
  mediaType: string;
  thumbnailUrl?: string;
  contributorIds?: string[];
  contributors?: ContributorItem[];
}

const normalizeSummaryActions = (raw: any): SummaryAction[] => {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((item: any) => {
    if (typeof item === 'string') return { title: item, description: '' };
    return { title: item.title || '', description: item.description || '' };
  });
};

export default function SeriesPage() {
  const [series, setSeries] = useState<VideoSeriesData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [contributorFilter, setContributorFilter] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<VideoSeriesData | null>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSeries();
  }, []);

  const loadSeries = async () => {
    try {
      setLoading(true);
      const all = await getAllContent();
      const seriesData = all.filter(c => (c as any).mediaType === 'videoSeries') as VideoSeriesData[];
      
      logger.log('Loaded video series:', seriesData.length);
      setSeries(seriesData);
    } catch (error: any) {
      logger.error('Error loading series:', error);
      toast({
        title: "Failed to load series",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSeries = () => {
    setSelectedSeries({
      id: '',
      title: '',
      description: '',
      contentLibraries: [],
      modules: [],
      episodes: {},
      mediaType: 'videoSeries',
    });
    setIsAddMode(true);
    setEditModalOpen(true);
  };

  const handleEditSeries = (seriesItem: VideoSeriesData) => {
    setSelectedSeries(seriesItem);
    setIsAddMode(false);
    setEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
    setSelectedSeries(null);
    setIsAddMode(false);
  };

  const handleSaveSeries = async (updatedSeries: VideoSeriesData) => {
    try {
      const libs = updatedSeries.contentLibraries || [];
      const videoSeriesCategories = await getVideoSeriesCategoriesForLibraries(libs);
      const contentCategories = videoSeriesCategories.map(cat => cat.tag);

      if (isAddMode) {
        await addVideoSeries({
          title: updatedSeries.title,
          description: updatedSeries.description || '',
          thumbnailUrl: updatedSeries.thumbnailUrl || '',
          contentLibraries: libs,
          modules: updatedSeries.modules || [],
          contentCategories,
          episodes: updatedSeries.episodes || {},
          publishDate: updatedSeries.publishDate ? Timestamp.fromDate(new Date(updatedSeries.publishDate)) : Timestamp.now(),
          status: updatedSeries.status || 'Draft',
          summaryText: updatedSeries.summaryText || '',
          summaryActions: updatedSeries.summaryActions || [],
          summaryNextSteps: updatedSeries.summaryNextSteps || {},
          summaryPrograms: updatedSeries.summaryPrograms || {},
          contributorIds: updatedSeries.contributorIds || [],
          contributors: updatedSeries.contributors || [],
          mediaType: 'videoSeries',
          episodeOnly: false,
          enabled: true,
        });

        toast({
          title: "Series created",
          description: `"${updatedSeries.title}" has been created successfully.`,
        });
      } else {
        await updateVideoSeries(updatedSeries.id, {
          title: updatedSeries.title,
          description: updatedSeries.description || '',
          thumbnailUrl: updatedSeries.thumbnailUrl || '',
          contentLibraries: libs,
          modules: updatedSeries.modules || [],
          contentCategories,
          episodes: updatedSeries.episodes || {},
          publishDate: updatedSeries.publishDate ? Timestamp.fromDate(new Date(updatedSeries.publishDate)) : null,
          status: updatedSeries.status || 'Draft',
          summaryText: updatedSeries.summaryText || '',
          summaryActions: updatedSeries.summaryActions || [],
          summaryNextSteps: updatedSeries.summaryNextSteps || {},
          summaryPrograms: updatedSeries.summaryPrograms || {},
          contributorIds: updatedSeries.contributorIds || [],
          contributors: updatedSeries.contributors || [],
          episodeOnly: false,
        });

        toast({
          title: "Series updated",
          description: `"${updatedSeries.title}" has been saved successfully.`,
        });
      }

      await loadSeries();
      handleCloseModal();
    } catch (error: any) {
      captureHandledException(error, "series.save");
      toast({
        title: isAddMode ? "Failed to create series" : "Failed to save series",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getEpisodeCount = (episodes?: EpisodesMap): number => {
    if (!episodes) return 0;
    return Object.keys(episodes).length;
  };

  // Build unique contributor list from all series
  const uniqueContributors = (() => {
    const seen = new Set<string>();
    const list: ContributorItem[] = [];
    series.forEach(s => {
      if (s.contributorIds && s.contributors) {
        s.contributorIds.forEach(id => {
          if (!seen.has(id)) {
            seen.add(id);
            const contrib = s.contributors!.find(c => c.id === id);
            if (contrib) list.push(contrib);
          }
        });
      }
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  })();

  const filteredSeries = series.filter(s => {
    const matchesSearch = s.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesContributor = !contributorFilter || s.contributorIds?.includes(contributorFilter);
    return matchesSearch && matchesContributor;
  });

  return (
    <main className="flex-1 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Layers className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Video Series</h1>
              <p className="text-slate-600">Manage video series and episodes</p>
            </div>
          </div>
          <Button onClick={handleAddSeries} data-testid="button-add-series">
            <Plus className="w-4 h-4 mr-2" />
            Add Series
          </Button>
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search series..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-series"
                />
              </div>
              {uniqueContributors.length > 0 && (
                <Select value={contributorFilter || "all"} onValueChange={(value) => setContributorFilter(value === "all" ? "" : value)}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="All Contributors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Contributors</SelectItem>
                    {uniqueContributors.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : filteredSeries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Layers className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {searchTerm ? 'No series match your search.' : 'No video series found.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredSeries.map((seriesItem) => (
              <Card key={seriesItem.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {seriesItem.thumbnailUrl ? (
                        <img
                          src={seriesItem.thumbnailUrl}
                          alt={seriesItem.title}
                          className="w-16 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-12 bg-slate-200 rounded flex items-center justify-center">
                          <Layers className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center space-x-3">
                          <h3 className="font-semibold text-slate-900">{seriesItem.title}</h3>
                          {seriesItem.contributors && seriesItem.contributors.length > 0 && (
                            <div className="flex items-center -space-x-2">
                              {seriesItem.contributors.slice(0, 5).map((contributor) => (
                                contributor.profilePicUrl ? (
                                  <img
                                    key={contributor.id}
                                    src={contributor.profilePicUrl}
                                    alt={contributor.name}
                                    title={contributor.name}
                                    className="w-7 h-7 rounded-full object-cover border-2 border-white"
                                  />
                                ) : (
                                  <div
                                    key={contributor.id}
                                    title={contributor.name}
                                    className="w-7 h-7 rounded-full bg-slate-300 border-2 border-white flex items-center justify-center text-xs font-semibold text-slate-600"
                                  >
                                    {contributor.name.charAt(0).toUpperCase()}
                                  </div>
                                )
                              ))}
                              {seriesItem.contributors.length > 5 && (
                                <div className="w-7 h-7 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-xs font-semibold text-slate-500">
                                  +{seriesItem.contributors.length - 5}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant="secondary">
                            {getEpisodeCount(seriesItem.episodes)} episode{getEpisodeCount(seriesItem.episodes) !== 1 ? 's' : ''}
                          </Badge>
                          {seriesItem.contentLibraries?.map((lib) => (
                            <Badge key={lib} variant="outline" className="text-xs">
                              {lib}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditSeries(seriesItem)}
                      data-testid={`button-edit-series-${seriesItem.id}`}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {selectedSeries && (
        <SeriesEditModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          series={selectedSeries}
          onSave={handleSaveSeries}
          isAddMode={isAddMode}
        />
      )}
    </main>
  );
}

interface SeriesEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  series: VideoSeriesData;
  onSave: (series: VideoSeriesData) => Promise<void>;
  isAddMode?: boolean;
}

function SeriesEditModal({ open, onOpenChange, series, onSave, isAddMode = false }: SeriesEditModalProps) {
  const [title, setTitle] = useState(series.title);
  const [description, setDescription] = useState(series.description || '');
  const [thumbnailUrl, setThumbnailUrl] = useState(series.thumbnailUrl || '');
  const [contentLibraries, setContentLibraries] = useState<string[]>(series.contentLibraries || []);
  const [modules, setModules] = useState<string[]>(series.modules || []);
  const [episodes, setEpisodes] = useState<EpisodeData[]>([]);
  const [publishDate, setPublishDate] = useState<string>(() => {
    if (series.publishDate) {
      if (series.publishDate.toDate) {
        return series.publishDate.toDate().toISOString().split('T')[0];
      }
      return new Date(series.publishDate).toISOString().split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  });
  const [availableLibraries, setAvailableLibraries] = useState<ContentLibraryData[]>([]);
  const [availableVideos, setAvailableVideos] = useState<ContentItem[]>([]);
  const [allContent, setAllContent] = useState<ContentItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoSearchTerm, setVideoSearchTerm] = useState('');
  const [summaryText, setSummaryText] = useState(series.summaryText || '');
  const [summaryActions, setSummaryActions] = useState<SummaryAction[]>(normalizeSummaryActions(series.summaryActions));
  const [summaryNextSteps, setSummaryNextSteps] = useState<SummaryNextStepsMap>(series.summaryNextSteps || {});
  const [nextStepsSearchTerm, setNextStepsSearchTerm] = useState('');
  const [summaryPrograms, setSummaryPrograms] = useState<SummaryProgramsMap>(series.summaryPrograms || {});
  const [programsSearchTerm, setProgramsSearchTerm] = useState('');
  const [availablePrograms, setAvailablePrograms] = useState<ProgramItem[]>([]);
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionDescription, setNewActionDescription] = useState('');
  const [originalEpisodeDates, setOriginalEpisodeDates] = useState<Record<string, any>>({});
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setTitle(series.title);
      setDescription(series.description || '');
      setThumbnailUrl(series.thumbnailUrl || '');
      setContentLibraries(series.contentLibraries || []);
      setModules(series.modules || []);
      
      // Set publish date - default to today for new series
      if (series.publishDate) {
        if (series.publishDate.toDate) {
          setPublishDate(series.publishDate.toDate().toISOString().split('T')[0]);
        } else {
          setPublishDate(new Date(series.publishDate).toISOString().split('T')[0]);
        }
      } else {
        setPublishDate(new Date().toISOString().split('T')[0]);
      }
      
      // Build a stable date map from the existing series so remove→re-add preserves dates
      const dateMap: Record<string, any> = {};
      if (series.episodes) {
        Object.values(series.episodes).forEach(ep => {
          if (ep.episodeAddedDate) dateMap[ep.contentId] = ep.episodeAddedDate;
        });
      }
      setOriginalEpisodeDates(dateMap);

      const episodesList = series.episodes
        ? Object.values(series.episodes).sort((a, b) => a.episode - b.episode)
        : [];
      setEpisodes(episodesList);
      
      // Set summary fields
      setSummaryText(series.summaryText || '');
      setSummaryActions(normalizeSummaryActions(series.summaryActions));
      setSummaryNextSteps(series.summaryNextSteps || {});
      setSummaryPrograms(series.summaryPrograms || {});
      setNewActionTitle('');
      setNewActionDescription('');
      setNextStepsSearchTerm('');
      setProgramsSearchTerm('');
      
      loadLibrariesAndVideos();
    }
  }, [open, series]);

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const downloadURL = await uploadSeriesImage(file);
      setThumbnailUrl(downloadURL);
      toast({
        title: "Image uploaded",
        description: "Thumbnail has been uploaded successfully.",
      });
    } catch (error: any) {
      logger.error('Error uploading thumbnail:', error);
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const loadLibrariesAndVideos = async () => {
    try {
      const [libraries, vimeoContent, allContentArr, programsArr] = await Promise.all([
        getContentLibraries(),
        getVimeoContent(),
        getAllContent(),
        getPrograms(),
      ]);

      setAvailableLibraries(libraries);

      const programs = (programsArr as any[]).map(p => ({
        id: p.id,
        name: p.name || '',
        thumbnailUrl: p.bannerUrl || undefined,
      })) as ProgramItem[];
      setAvailablePrograms(programs.filter(p => p.name).sort((a, b) => a.name.localeCompare(b.name)));
      
      setAvailableVideos(vimeoContent as ContentItem[]);
      setAllContent(allContentArr as ContentItem[]);
    } catch (error) {
      logger.error('Error loading data:', error);
    }
  };

  const getLibraryKey = (library: ContentLibraryData): string => {
    return library.key || library.name || library.id || '';
  };

  const handleLibraryToggle = (libraryIdentifier: string) => {
    const library = availableLibraries.find(l => getLibraryKey(l) === libraryIdentifier);
    
    if (contentLibraries.includes(libraryIdentifier)) {
      setContentLibraries(contentLibraries.filter(l => l !== libraryIdentifier));
      if (library) {
        const otherLibrariesWithSameModule = availableLibraries.filter(
          l => getLibraryKey(l) !== libraryIdentifier && 
               l.module === library.module && 
               contentLibraries.includes(getLibraryKey(l))
        );
        if (otherLibrariesWithSameModule.length === 0) {
          setModules(modules.filter(m => m !== library.module));
        }
      }
    } else {
      setContentLibraries([...contentLibraries, libraryIdentifier]);
      if (library && !modules.includes(library.module)) {
        setModules([...modules, library.module]);
      }
    }
  };

  const handleAddEpisode = (video: ContentItem) => {
    if (episodes.some(e => e.contentId === video.id)) {
      toast({
        title: "Already added",
        description: "This video is already in the episode list.",
        variant: "destructive",
      });
      return;
    }

    // Preserve existing date (original or from earlier in this session); only assign now() for truly new episodes
    const existingDate = originalEpisodeDates[video.id];
    const episodeAddedDate = existingDate ?? Timestamp.now();

    if (!existingDate) {
      setOriginalEpisodeDates(prev => ({ ...prev, [video.id]: episodeAddedDate }));
    }

    const newEpisode: EpisodeData = {
      contentId: video.id,
      episode: episodes.length + 1,
      episodeAddedDate,
    };
    setEpisodes([...episodes, newEpisode]);
  };

  const handleRemoveEpisode = (contentId: string) => {
    const updated = episodes
      .filter(e => e.contentId !== contentId)
      .map((e, index) => ({ ...e, episode: index + 1 }));
    setEpisodes(updated);
  };

  const handleMoveEpisode = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= episodes.length) return;

    const updated = [...episodes];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setEpisodes(updated.map((e, i) => ({ ...e, episode: i + 1 })));
  };

  const handleAddAction = () => {
    if (!newActionTitle.trim()) return;
    setSummaryActions([...summaryActions, { title: newActionTitle.trim(), description: newActionDescription.trim() }]);
    setNewActionTitle('');
    setNewActionDescription('');
  };

  const handleRemoveAction = (index: number) => {
    setSummaryActions(summaryActions.filter((_, i) => i !== index));
  };

  const handleUpdateAction = (index: number, field: 'title' | 'description', value: string) => {
    const updated = [...summaryActions];
    updated[index] = { ...updated[index], [field]: value };
    setSummaryActions(updated);
  };

  const handleAddNextStep = (content: ContentItem) => {
    if (summaryNextSteps[content.id]) {
      toast({
        title: "Already added",
        description: "This content is already in next steps.",
        variant: "destructive",
      });
      return;
    }
    const nextStep: SummaryNextStep = {
      contentId: content.id,
      mediaType: content.mediaType,
      title: content.title
    };
    if (content.durationMinutes !== undefined) nextStep.durationMinutes = content.durationMinutes;
    if (content.externalId) nextStep.externalId = content.externalId;
    if (content.thumbnailUrl) nextStep.thumbnailUrl = content.thumbnailUrl;
    
    setSummaryNextSteps({
      ...summaryNextSteps,
      [content.id]: nextStep
    });
    setNextStepsSearchTerm('');
  };

  const handleRemoveNextStep = (contentId: string) => {
    const updated = { ...summaryNextSteps };
    delete updated[contentId];
    setSummaryNextSteps(updated);
  };

  const handleAddProgram = (program: ProgramItem) => {
    if (summaryPrograms[program.id]) {
      toast({
        title: "Already added",
        description: "This program is already linked.",
        variant: "destructive",
      });
      return;
    }
    const entry: SummaryProgram = { programId: program.id, name: program.name };
    if (program.thumbnailUrl) entry.thumbnailUrl = program.thumbnailUrl;
    setSummaryPrograms({ ...summaryPrograms, [program.id]: entry });
    setProgramsSearchTerm('');
  };

  const handleRemoveProgram = (programId: string) => {
    const updated = { ...summaryPrograms };
    delete updated[programId];
    setSummaryPrograms(updated);
  };

  const handleSave = async (status: 'Draft' | 'Published') => {
    if (!title.trim()) {
      toast({
        title: "Validation Error",
        description: "Title is required.",
        variant: "destructive",
      });
      return;
    }

    if (!description.trim()) {
      toast({
        title: "Validation Error",
        description: "Description is required.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const episodesMap: EpisodesMap = {};
      episodes.forEach(ep => {
        episodesMap[ep.contentId] = {
          contentId: ep.contentId,
          episode: ep.episode,
          ...(ep.episodeAddedDate && { episodeAddedDate: ep.episodeAddedDate }),
        };
      });

      // Aggregate unique contributors from all episode videos
      const seenIds = new Set<string>();
      const uniqueContributorIds: string[] = [];
      const uniqueContributors: ContributorItem[] = [];
      episodes.forEach(ep => {
        const video = availableVideos.find(v => v.id === ep.contentId);
        if (video?.contributorIds) {
          video.contributorIds.forEach(cid => {
            if (!seenIds.has(cid)) {
              seenIds.add(cid);
              uniqueContributorIds.push(cid);
              const contrib = video.contributors?.find(c => c.id === cid);
              if (contrib) uniqueContributors.push(contrib);
            }
          });
        }
      });

      await onSave({
        ...series,
        title,
        description: description.trim(),
        thumbnailUrl,
        contentLibraries,
        modules,
        episodes: episodesMap,
        publishDate,
        status,
        summaryText: summaryText || undefined,
        summaryActions: summaryActions.length > 0 ? summaryActions : undefined,
        summaryNextSteps: Object.keys(summaryNextSteps).length > 0 ? summaryNextSteps : undefined,
        summaryPrograms: Object.keys(summaryPrograms).length > 0 ? summaryPrograms : undefined,
        contributorIds: uniqueContributorIds,
        contributors: uniqueContributors,
      });
    } finally {
      setSaving(false);
    }
  };

  const getVideoDetails = (contentId: string): ContentItem | undefined => {
    return availableVideos.find(v => v.id === contentId);
  };

  const filteredVideos = availableVideos.filter(v =>
    v.title.toLowerCase().includes(videoSearchTerm.toLowerCase()) ||
    v.author?.toLowerCase().includes(videoSearchTerm.toLowerCase())
  );

  const filteredNextStepsContent = allContent.filter(c =>
    (c.title.toLowerCase().includes(nextStepsSearchTerm.toLowerCase()) ||
    c.mediaType?.toLowerCase().includes(nextStepsSearchTerm.toLowerCase())) &&
    !summaryNextSteps[c.id]
  );

  const filteredPrograms = availablePrograms.filter(p =>
    p.name.toLowerCase().includes(programsSearchTerm.toLowerCase()) &&
    !summaryPrograms[p.id]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Layers className="w-5 h-5" />
            <span>{isAddMode ? 'Add New Series' : 'Edit Series'}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Series title"
                data-testid="input-series-title"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              aria-required="true"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Series description"
              rows={3}
              data-testid="input-series-description"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="publishDate">Publish Date</Label>
            <Input
              id="publishDate"
              type="date"
              value={publishDate}
              onChange={(e) => setPublishDate(e.target.value)}
              data-testid="input-series-publish-date"
            />
          </div>

          <div className="space-y-2">
            <Label>Thumbnail</Label>
            <div className="flex items-start space-x-4">
              {thumbnailUrl ? (
                <div className="relative">
                  <img
                    src={thumbnailUrl}
                    alt="Series thumbnail"
                    className="w-32 h-20 object-cover rounded border"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-red-100 hover:bg-red-200"
                    onClick={() => setThumbnailUrl('')}
                    data-testid="button-remove-thumbnail"
                  >
                    <X className="w-3 h-3 text-red-600" />
                  </Button>
                </div>
              ) : (
                <div className="w-32 h-20 border-2 border-dashed rounded flex items-center justify-center bg-slate-50">
                  <Image className="w-8 h-8 text-slate-300" />
                </div>
              )}
              <div className="flex-1">
                <label
                  htmlFor="thumbnail-upload"
                  className={`inline-flex items-center px-4 py-2 border rounded-md cursor-pointer transition-colors ${
                    uploading
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-white hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Image'}
                </label>
                <input
                  id="thumbnail-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleThumbnailUpload}
                  disabled={uploading}
                  data-testid="input-thumbnail-upload"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Recommended: 16:9 aspect ratio, max 5MB
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Content Libraries</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md min-h-[60px]">
              {availableLibraries.map((library) => {
                const libraryIdentifier = getLibraryKey(library);
                return (
                  <Badge
                    key={library.id || libraryIdentifier}
                    variant={contentLibraries.includes(libraryIdentifier) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => handleLibraryToggle(libraryIdentifier)}
                    data-testid={`badge-library-${libraryIdentifier}`}
                  >
                    {library.name || library.title || library.key}
                  </Badge>
                );
              })}
            </div>
            {modules.length > 0 && (
              <p className="text-sm text-slate-600">
                Modules: {modules.join(', ')}
              </p>
            )}
          </div>

          <div className="border-t pt-4">
            <Label className="text-lg font-semibold">Episodes</Label>
            
            <div className="mt-4 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search videos to add..."
                  value={videoSearchTerm}
                  onChange={(e) => setVideoSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-videos"
                />
              </div>
              
              {videoSearchTerm && (
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-md">
                  {filteredVideos.slice(0, 10).map((video) => {
                    const isAdded = episodes.some(e => e.contentId === video.id);
                    return (
                      <div
                        key={video.id}
                        className={`flex items-center justify-between p-2 hover:bg-slate-50 ${isAdded ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center space-x-2">
                          {video.thumbnailUrl && (
                            <img src={video.thumbnailUrl} alt="" className="w-12 h-8 object-cover rounded" />
                          )}
                          <div>
                            <p className="text-sm font-medium">{video.title}</p>
                            <p className="text-xs text-slate-500">{video.author}</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isAdded}
                          onClick={() => handleAddEpisode(video)}
                          data-testid={`button-add-video-${video.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {episodes.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <p className="text-slate-600">No episodes added yet. Search for videos above to add them.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {episodes.map((episode, index) => {
                  const video = getVideoDetails(episode.contentId);
                  return (
                    <div
                      key={episode.contentId}
                      className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex flex-col space-y-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          disabled={index === 0}
                          onClick={() => handleMoveEpisode(index, 'up')}
                          data-testid={`button-move-up-${index}`}
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          disabled={index === episodes.length - 1}
                          onClick={() => handleMoveEpisode(index, 'down')}
                          data-testid={`button-move-down-${index}`}
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <Badge variant="secondary" className="min-w-[40px] justify-center">
                        {episode.episode}
                      </Badge>
                      
                      {video?.thumbnailUrl && (
                        <img src={video.thumbnailUrl} alt="" className="w-16 h-10 object-cover rounded" />
                      )}
                      
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">
                          {video?.title || 'Unknown video'}
                        </p>
                        {video?.author && (
                          <p className="text-sm text-slate-600">{video.author}</p>
                        )}
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRemoveEpisode(episode.contentId)}
                        data-testid={`button-remove-episode-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Summary Section */}
          <div className="space-y-4">
            <Label className="text-lg font-semibold">Summary (Optional)</Label>
            
            <div className="space-y-2">
              <Label htmlFor="summaryText">Summary Text</Label>
              <Textarea
                id="summaryText"
                value={summaryText}
                onChange={(e) => setSummaryText(e.target.value)}
                placeholder="Enter a summary paragraph for this series..."
                rows={4}
                data-testid="input-summary-text"
              />
            </div>

            <div className="space-y-3">
              <Label>Summary Actions</Label>

              {summaryActions.length > 0 && (
                <div className="space-y-3">
                  {summaryActions.map((action, index) => (
                    <div key={index} className="border rounded-lg p-3 space-y-2 bg-slate-50">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Action {index + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                          onClick={() => handleRemoveAction(index)}
                          data-testid={`button-remove-action-${index}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Input
                          value={action.title}
                          onChange={(e) => handleUpdateAction(index, 'title', e.target.value)}
                          placeholder="Action title"
                          className="text-sm"
                          data-testid={`input-action-title-${index}`}
                        />
                        <Textarea
                          value={action.description}
                          onChange={(e) => handleUpdateAction(index, 'description', e.target.value)}
                          placeholder="Action description"
                          rows={2}
                          className="text-sm resize-none"
                          data-testid={`input-action-description-${index}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="border rounded-lg p-3 space-y-2 border-dashed">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">New Action</span>
                <Input
                  value={newActionTitle}
                  onChange={(e) => setNewActionTitle(e.target.value)}
                  placeholder="Action title *"
                  className="text-sm"
                  data-testid="input-new-action-title"
                />
                <Textarea
                  value={newActionDescription}
                  onChange={(e) => setNewActionDescription(e.target.value)}
                  placeholder="Action description"
                  rows={2}
                  className="text-sm resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddAction();
                    }
                  }}
                  data-testid="input-new-action-description"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddAction}
                  disabled={!newActionTitle.trim()}
                  className="gap-1"
                  data-testid="button-add-action"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Action
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Summary Next Steps (Content Suggestions)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search content to add as next steps..."
                  value={nextStepsSearchTerm}
                  onChange={(e) => setNextStepsSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-next-steps-search"
                />
              </div>
              
              {nextStepsSearchTerm && filteredNextStepsContent.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {filteredNextStepsContent.slice(0, 10).map(content => (
                    <div
                      key={content.id}
                      className="flex items-center justify-between p-2 hover:bg-slate-50 rounded cursor-pointer"
                      onClick={() => handleAddNextStep(content)}
                      data-testid={`next-step-option-${content.id}`}
                    >
                      <div className="flex items-center space-x-2">
                        {content.thumbnailUrl && (
                          <img src={content.thumbnailUrl} alt="" className="w-10 h-6 object-cover rounded" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{content.title}</p>
                          <p className="text-xs text-slate-500">{content.mediaType}</p>
                        </div>
                      </div>
                      <Plus className="w-4 h-4 text-slate-400" />
                    </div>
                  ))}
                </div>
              )}

              {Object.keys(summaryNextSteps).length > 0 && (
                <div className="space-y-2 mt-2">
                  {Object.values(summaryNextSteps).map((step) => (
                    <div
                      key={step.contentId}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{step.title}</p>
                        <p className="text-sm text-slate-600">{step.mediaType}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRemoveNextStep(step.contentId)}
                        data-testid={`button-remove-next-step-${step.contentId}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Summary Programs</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search programs to link..."
                  value={programsSearchTerm}
                  onChange={(e) => setProgramsSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="input-programs-search"
                />
              </div>

              {programsSearchTerm && filteredPrograms.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {filteredPrograms.slice(0, 10).map(program => (
                    <div
                      key={program.id}
                      className="flex items-center justify-between p-2 hover:bg-slate-50 rounded cursor-pointer"
                      onClick={() => handleAddProgram(program)}
                      data-testid={`program-option-${program.id}`}
                    >
                      <div className="flex items-center space-x-2">
                        {program.thumbnailUrl && (
                          <img src={program.thumbnailUrl} alt="" className="w-10 h-6 object-cover rounded" />
                        )}
                        <p className="text-sm font-medium">{program.name}</p>
                      </div>
                      <Plus className="w-4 h-4 text-slate-400" />
                    </div>
                  ))}
                </div>
              )}

              {programsSearchTerm && filteredPrograms.length === 0 && (
                <p className="text-sm text-slate-400 px-1">No programs found.</p>
              )}

              {Object.keys(summaryPrograms).length > 0 && (
                <div className="space-y-2 mt-2">
                  {Object.values(summaryPrograms).map((program) => (
                    <div
                      key={program.programId}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-2">
                        {program.thumbnailUrl && (
                          <img src={program.thumbnailUrl} alt="" className="w-10 h-6 object-cover rounded" />
                        )}
                        <p className="font-medium text-slate-900">{program.name}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRemoveProgram(program.programId)}
                        data-testid={`button-remove-program-${program.programId}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleSave('Draft')} 
              disabled={saving}
              data-testid="button-save-draft"
            >
              {saving ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button 
              onClick={() => handleSave('Published')} 
              disabled={saving}
              data-testid="button-publish"
            >
              {saving ? 'Saving...' : 'Publish'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
